import React, { useContext, useMemo } from "react";
import AccountFormProvider from "@shared/features/common/AccountFormProvider.tsx";
import styled, { useTheme } from "styled-components";
import { StyledOnboardingSteps } from "features/onboarding/OnboardingSteps.tsx";
import InvitingUserSummary from "features/configuration/InviteExternalUsers/InvitingUserSummary.tsx";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery.ts";
import ExternalConnectAccountProvider from "features/configuration/InviteExternalUsers/ExternalConnectAccountProvider.tsx";
import SuccessPage from "features/configuration/InviteExternalUsers/SuccessPage.tsx";
import cornixLogo from "images/svg_logo.svg";
import cornixWhiteLogo from "images/svg_white_logo.svg";
import ArrowLeftOutlined from "@ant-design/icons/ArrowLeftOutlined";
import { ArrowLeft } from "lucide-react";
import LookIcon from "common/Icons/LookIcon";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";
import { AccountFormContext } from "@shared/features/common/AccountForm.context.ts";
import useMedia from "utils/hooks/useMedia.ts";
import {
  AUTH_PATHS,
  EXTERNAL_INVITE_CONFIRM_USER_PATH,
  EXTERNAL_INVITE_SUCCESS_PATH,
  GENERAL_BACKEND_ERROR,
  LARGE_MOBILE,
  MOBILE_SEARCH,
  ONBOARDING_PATHS,
  ONBOARDING_SELECT_EXCHANGE_PATH,
} from "common/constants.ts";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context.ts";
import { ConnectExternalAccountStep } from "@shared/features/configuration/types.ts";
import { BrokenLink } from "common/Icons/Icons.tsx";
import Title from "common/Typography/Title.tsx";
import Text from "common/Typography/Text.tsx";
import { useWindowSize } from "react-use";
import { BOTTOM_SPACER_HEIGHT } from "common/Home/General/constants.tsx";
import ExternalInviteCreateAccountStep from "features/configuration/InviteExternalUsers/CreateAccountStep.tsx";
import { Route, Switch } from "react-router-dom";
import Auth from "features/auth/Auth.tsx";
import OnboardingProvider from "features/onboarding/OnboardingProvider.tsx";
import UserInfo from "features/userInfo/UserInfo.tsx";
import { useSelectUser } from "@shared/utils/hooks/misc.ts";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";
import LanguageSelectorPopover from "common/Home/Header/LanguageSelectorPopover.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

const ExternalConnectAccount = () => {
  return (
    <ExternalConnectAccountProvider>
      <AccountFormProvider>
        <InnerExternalConnectAccount />
      </AccountFormProvider>
    </ExternalConnectAccountProvider>
  );
};

export default ExternalConnectAccount;

const InnerExternalConnectAccount = () => {
  const theme = useTheme();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { appendQuery } = useHistoryWithQuery();
  const { resetFormValues } = useContext(AccountFormContext)!;
  const { step, isError, errorMessage, isWhitelabel, hasReadAccess } =
    useContext(ExternalConnectAccountContext)!;
  const { height } = useWindowSize();
  const { brandingData } = useBranding();
  const { t } = useTypedTranslation(LanguageNameSpaces.AUTH);
  const logo = theme.isDarkMode
    ? brandingData?.dark_mode_logo
    : brandingData?.light_mode_logo;
  const user = useSelectUser();
  const hideLogo = isWhitelabel && !logo;
  const showUserInfo = !!user && hasReadAccess;

  const onBackClick = () => {
    resetFormValues();
    appendQuery(
      undefined,
      ["exchange"],
      undefined,
      ONBOARDING_SELECT_EXCHANGE_PATH
    );
  };

  const getBackButton = () => {
    if (step !== ConnectExternalAccountStep.CONNECT_EXCHANGE) return null;
    return (
      <BackWrapper>
        <BackButton onClick={onBackClick}>
          <LookIcon
            classic={<ArrowLeftOutlined style={{ marginRight: 8 }} />}
            newLook={
              <ArrowLeft
                size={16}
                strokeWidth={NEW_LOOK_ICON_STROKE}
                aria-hidden
                style={{ marginRight: 6 }}
              />
            }
          />
          {t("external_connect.steps.select_exchange")}
        </BackButton>
      </BackWrapper>
    );
  };

  const getStepTitle = (step: ConnectExternalAccountStep) => {
    switch (step) {
      case ConnectExternalAccountStep.CREATE_ACCOUNT:
        return t("external_connect.steps.create_account");
      case ConnectExternalAccountStep.SELECT_EXCHANGE:
        return t("external_connect.steps.select_exchange");
      case ConnectExternalAccountStep.CONNECT_EXCHANGE:
        return t("external_connect.steps.connect_exchange");
      case ConnectExternalAccountStep.SUCCESS:
        return t("external_connect.steps.success");
    }
  };

  const allSteps = useMemo(() => {
    return Object.values(ConnectExternalAccountStep).filter(
      (step) =>
        step !== ConnectExternalAccountStep.SUCCESS &&
        (hasReadAccess || step !== ConnectExternalAccountStep.CREATE_ACCOUNT)
    );
  }, [hasReadAccess]);

  const getSteps = () => {
    const stepIndex = allSteps.findIndex((iterStep) => iterStep === step);

    return (
      <StyledOnboardingSteps
        size="small"
        titlePlacement="vertical"
        current={stepIndex}
        responsive={false}
        $isMobile={false}
        items={allSteps.map((step) => ({ title: getStepTitle(step) }))}
        style={{ maxWidth: hasReadAccess ? 400 : 300 }}
      />
    );
  };

  const getContent = () => {
    if (isError) {
      const isGeneralError = errorMessage === GENERAL_BACKEND_ERROR;
      return (
        <ErrorWrapper>
          <div style={{ marginTop: -(height / 8), maxWidth: 370 }}>
            <BrokenLink
              style={{
                width: isMobile ? 80 : 140,
                height: "auto",
                marginBottom: 16,
              }}
            />
            <Title level={isMobile ? 2 : 1} style={{ textAlign: "center" }}>
              {isGeneralError
                ? t("external_connect.errors.general_title")
                : t("external_connect.errors.invitation_not_valid")}
            </Title>
            <Text style={{ maxWidth: isMobile ? 300 : 360 }}>
              {isGeneralError
                ? t("external_connect.errors.general_description")
                : errorMessage}
            </Text>
          </div>
        </ErrorWrapper>
      );
    }

    return (
      <ContentWrapper $hideLogo={hideLogo}>
        <InnerContentWrapper>
          {getBackButton()}
          {step !== ConnectExternalAccountStep.SUCCESS && (
            <>
              {getSteps()}
              <InvitingUserSummary
                style={{ margin: isMobile ? "16px 0" : "24px 0" }}
              />
            </>
          )}
          <Switch>
            <Route
              exact
              path={ONBOARDING_PATHS}
              component={OnboardingProvider}
            />
            <Route exact path={AUTH_PATHS} component={Auth} />
            <Route
              exact
              path={EXTERNAL_INVITE_CONFIRM_USER_PATH}
              component={ExternalInviteCreateAccountStep}
            />
            <Route
              exact
              path={EXTERNAL_INVITE_SUCCESS_PATH}
              component={SuccessPage}
            />
          </Switch>
        </InnerContentWrapper>
      </ContentWrapper>
    );
  };

  const getHeader = () => {
    return (
      <HeaderRow>
        {hideLogo ? (
          <div />
        ) : (
          <LogoWrapper>
            <Logo
              src={logo ?? (theme.isDarkMode ? cornixWhiteLogo : cornixLogo)}
            />
          </LogoWrapper>
        )}
        <HeaderActions>
          {showUserInfo ? (
            <UserInfo
              user={user}
              hideSettings={true}
              style={{ paddingLeft: 20 }}
            />
          ) : (
            <LanguageSelectorPopover />
          )}
        </HeaderActions>
      </HeaderRow>
    );
  };

  return (
    <Wrapper>
      {getHeader()}
      {getContent()}
    </Wrapper>
  );
};

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 16px 10px 16px;
`;
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;
const LogoWrapper = styled.div`
  flex: 1;
`;
const Logo = styled.img`
  max-height: 70px;
  height: auto;
  max-width: 100%;
`;
const Wrapper = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
`;
const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex: 1;
`;
const ContentWrapper = styled.div<{ $hideLogo?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: ${(p) => (p.$hideLogo ? 36 : 20)}px 20px ${BOTTOM_SPACER_HEIGHT}px;
  overflow-y: auto;
  @media (max-width: 700px) {
    padding: ${(p) => (p.$hideLogo ? 24 : 8)}px 12px ${BOTTOM_SPACER_HEIGHT}px;
  }
`;
const InnerContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;
const BackWrapper = styled.div`
  width: 100%;
  padding-bottom: 16px;
  @media (min-width: 700px) {
    padding-bottom: 0;
  }
`;
const BackButton = styled.div`
  display: flex;
  align-items: center;
  color: ${(p) => p.theme.colors.primaryBlue};
  cursor: pointer;
  @media (min-width: ${LARGE_MOBILE}) {
    position: absolute;
  }
`;
